import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Building2, Briefcase, Send, UserCircle, Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import ContactsManagement from '../components/ContactsManagement';
import CustomerManagement from './CustomerManagement';
import JobsTableView from '../components/recruiting/JobsTableView';
import OverviewTab from '../components/recruiting/OverviewTab';
import SalesTeamTab from '../components/sales/SalesTeamTab';
import UserSelector from '../components/sales/UserSelector';
import SubmissionsTab from '../components/recruiting/SubmissionsTab';
import CandidatesTab from '../components/recruiting/CandidatesTab';
import { 
  fetchAccountManagers as fetchAccountManagersService,
  fetchClients as fetchClientsService,
  fetchJobs as fetchJobsService,
  fetchSubmissions as fetchSubmissionsService,
  fetchTeamData as fetchTeamDataService
} from '../utils/salesDataService';

const UnifiedAccountManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Data states
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [myTeam, setMyTeam] = useState(null);

  // Fetch all account managers on mount
  useEffect(() => {
    loadAccountManagers();
  }, []);

  // Fetch data when user changes
  useEffect(() => {
    if (selectedUser) {
      // STRONG BRIDGE: Security check - validate user has account manager roles
      const userRoles = selectedUser.roles || [selectedUser.role];
      const isAccountManager = userRoles.some(role => 
        role === 'account_manager' || role === 'lead_account_manager'
      );
      
      if (!isAccountManager) {
        console.error(`[SECURITY] User ${selectedUser.full_name} attempted to access Account Manager dashboard without proper roles:`, userRoles);
        toast.error(`Access denied: ${selectedUser.full_name} is not an Account Manager`);
        return;
      }
      
      // STRONG BRIDGE: Clear stale data before loading new user's data
      setJobs([]);
      setCandidates([]);
      setSubmissions([]);
      setClients([]);
      setMyTeam(null);
      setTeamMembers([]);
      
      // Then load fresh data
      loadUserData();
    }
  }, [selectedUser]);

  const loadAccountManagers = async () => {
    try {
      const accountManagers = await fetchAccountManagersService();
      setAvailableUsers(accountManagers);
      
      // Auto-select first user
      if (accountManagers.length > 0 && !selectedUser) {
        setSelectedUser(accountManagers[0]);
      }
    } catch (error) {
      toast.error('Failed to load account managers');
    }
  };

  const loadUserData = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const [clientsData, jobsData, candidatesData, submissionsData, teamData] = await Promise.all([
        fetchClientsService(selectedUser),
        fetchJobsService(selectedUser),
        fetch(`${BACKEND_URL}/api/candidates`).then(r => r.ok ? r.json() : []),
        fetchSubmissionsService(),
        fetchTeamDataService(selectedUser)
      ]);
      
      setClients(clientsData);
      setJobs(jobsData);
      setCandidates(Array.isArray(candidatesData) ? candidatesData : []);
      setSubmissions(submissionsData);
      setMyTeam(teamData.team);
      setTeamMembers(teamData.members);
      
      // STRONG BRIDGE: Audit log for debugging
      console.log(`[AccountManager] Loaded data for: ${selectedUser.full_name}`);
      console.log(`  - Jobs: ${jobsData.length}`);
      console.log(`  - Candidates: ${candidatesData.length}`);
      console.log(`  - Submissions: ${submissionsData.length}`);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with User Proxy Selector */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Manager Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">
                {selectedUser.industries && selectedUser.industries.length > 0 && (
                  <span>Industries: {selectedUser.industries.join(', ')}</span>
                )}
              </p>
            </div>
            
            {/* User Proxy Dropdown */}
            <UserSelector
              selectedUser={selectedUser}
              availableUsers={availableUsers}
              onUserChange={setSelectedUser}
              showDropdown={showUserDropdown}
              onToggleDropdown={setShowUserDropdown}
              role="Account Manager"
            />
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-1 -mb-px">
            {[
              { key: 'overview', label: 'Overview', icon: TrendingUp },
              { key: 'jobs', label: 'Job Requests', icon: Briefcase },
              { key: 'candidates', label: 'Candidates', icon: Users },
              { key: 'submissions', label: 'Submissions', icon: Send },
              { key: 'contacts', label: 'Contacts', icon: UserCircle },
              { key: 'companies', label: 'Companies', icon: Building2 },
              ...(selectedUser.role === 'lead_account_manager' ? [{ key: 'team', label: 'My Team', icon: UserCircle }] : [])
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-orange-600 text-orange-600 bg-orange-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="account-manager-dashboard">
        {activeTab === 'overview' && (
          <OverviewTab 
            selectedUser={selectedUser} 
            jobs={jobs} 
            submissions={submissions}
            candidates={candidates}
            myTeam={myTeam}
            teamLead={null}
            availableUsers={availableUsers}
          />
        )}
        {activeTab === 'jobs' && (
          <JobsTableView 
            selectedUser={selectedUser} 
            jobs={jobs} 
            initialFilters={{}} 
            onViewJob={() => {}} 
          />
        )}
        {activeTab === 'candidates' && (
          <CandidatesTab 
            selectedUser={selectedUser} 
            candidates={candidates} 
            onRefresh={loadUserData} 
            onAddCandidate={() => toast.info('Add Candidate feature coming soon')} 
          />
        )}
        {activeTab === 'submissions' && (
          <SubmissionsTab 
            candidates={candidates} 
            jobs={jobs} 
            submissions={submissions} 
          />
        )}
        {activeTab === 'contacts' && <ContactsManagement />}
        {activeTab === 'companies' && <CustomerManagement readOnly={false} />}
        {activeTab === 'team' && selectedUser.role === 'lead_account_manager' && (
          <SalesTeamTab 
            selectedUser={selectedUser} 
            myTeam={myTeam} 
            teamMembers={teamMembers} 
          />
        )}
      </div>
    </div>
  );
};

export default UnifiedAccountManagerDashboard;
